import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "./Produttivita.css";
import FiltroCategorie from "../components/FiltroCategorie/FiltroCategorie.js";
import { getCardsForSection, getUserPermissions } from "../config/cardRegistry";
import { getTagStyles, UNTAGGED_STYLES, TAG_PALETTE } from "../utils/tagUtils";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const Produttivita = () => {
    const navigate = useNavigate();
    const permissions = getUserPermissions();
    const role = localStorage.getItem("role") || "user";
    const isSuperAdmin = role === "superadmin";

    const [tags, setTags] = useState([]);
    const [visiblePages, setVisiblePages] = useState(() => getCardsForSection("produttivita", permissions));
    const [filteredPages, setFilteredPages] = useState([]);
    const [selectedCategory, setSelectedCategoryState] = useState("ALL");

    const [showTagModal, setShowTagModal] = useState(false);
    const [editingTag, setEditingTag] = useState(null); // null = creazione, oggetto = modifica

    // stato dropdown assegna tag
    const [dropdownCardKey, setDropdownCardKey] = useState(null);

    const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 480px)").matches);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 480px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // stato conferma eliminazione
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, cardCount }

    const hasAccess = visiblePages.length > 0;

    // tag dal backend
    useEffect(() => {
        fetchTags();
    }, []);

    // aggiorna filteredPages quando cambiano visiblePages o selectedCategory
    useEffect(() => {
        applyFilter(selectedCategory);
    }, [visiblePages]);

    const fetchTags = async () => {
        try {
            const res = await axios.get(`${API_URL}/tags?section=produttivita`, getAuthHeaders());
            setTags(res.data);
        } catch (err) {
            console.error("Errore nel caricamento tag:", err);
        }
    };


    // Dopo una modifica ai tag, ricarica sia i tag che il tagMap nel localStorage,
    // così le card si aggiornano senza dover rifare il login.
    const refreshAfterTagChange = async () => {
        await fetchTags();
        try {
            const tagRes = await axios.get(`${API_URL}/tags?section=produttivita`, getAuthHeaders());
            const tagById = {};
            tagRes.data.forEach(t => { tagById[t.id] = { id: t.id, name: t.name, color: t.color }; });

            // ricarica permissions per ricostruire il tagMap
            const permRes = await axios.get(`${API_URL}/roles/permissions`, getAuthHeaders());
            const allPerms = Object.values(permRes.data).flat();

            // Aggiorna solo le entry di produttivita, lasciando intatte le altre sezioni
            const currentTagMap = JSON.parse(localStorage.getItem("tagMap") || "{}");
            const newTagMap = Object.fromEntries(
                Object.entries(currentTagMap).filter(([key]) => !key.startsWith("produttivita."))
            );
            allPerms
                .filter(p => p.section === "produttivita" && p.tag_id)
                .forEach(p => {
                    if (tagById[p.tag_id]) newTagMap[p.card_key] = tagById[p.tag_id];
                });
            localStorage.setItem("tagMap", JSON.stringify(newTagMap));
        } catch (err) {
            console.error("Errore nell'aggiornamento tagMap:", err);
        }

        // ricalcola le card visibili con i nuovi dati tag
        setVisiblePages(getCardsForSection("produttivita", permissions));
    };

    // calcola categorie dinamiche dai tag delle card visibili
    const getCategories = () => {
        const tagNames = [...new Set(visiblePages.filter(p => p.tag).map(p => p.tag.name))];
        const hasUntagged = visiblePages.some(p => !p.tag);
        const cats = ["ALL", ...tagNames.sort()];
        if (hasUntagged) cats.push("SENZA TAG");
        return cats;
    };

    const categories = hasAccess ? getCategories() : [];

    const applyFilter = (category) => {
        if (category === "SENZA TAG") {
            setFilteredPages(visiblePages.filter(p => !p.tag));
        } else if (category !== "ALL") {
            setFilteredPages(visiblePages.filter(p => p.tag && p.tag.name === category));
        } else {
            setFilteredPages(visiblePages);
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategoryState(category);
        applyFilter(category);
    };

    // Handler CRUD tag
    const handleCreateTag = () => {
        setEditingTag(null);
        setShowTagModal(true);
    };

    const handleEditTag = (tag) => {
        setEditingTag(tag);
        setShowTagModal(true);
    };

    const handleDeleteTag = (tag) => {
        setDeleteConfirm({ id: tag.id, name: tag.name, cardCount: parseInt(tag.card_count) });
    };

    const confirmDeleteTag = async () => {
        try {
            await axios.delete(`${API_URL}/tags/${deleteConfirm.id}`, getAuthHeaders());
            setDeleteConfirm(null);
            await refreshAfterTagChange();
        } catch (err) {
            console.error("Errore nell'eliminazione tag:", err);
        }
    };

    const handleSaveTag = async (name, color) => {
        try {
            if (editingTag) {
                await axios.put(`${API_URL}/tags/${editingTag.id}`, { name, color }, getAuthHeaders());
            } else {
                await axios.post(`${API_URL}/tags`, { name, color, section: "produttivita" }, getAuthHeaders());
            }
            setShowTagModal(false);
            setEditingTag(null);
            await refreshAfterTagChange();
        } catch (err) {
            const msg = err.response?.data?.error || "Errore nel salvataggio";
            alert(msg);
        }
    };

    const handleAssignTag = async (cardKey, tagId) => {
        try {
            await axios.put(`${API_URL}/tags/assign`, { tagId, cardKey }, getAuthHeaders());
            setDropdownCardKey(null);
            await refreshAfterTagChange();
        } catch (err) {
            console.error("Errore nell'assegnazione tag:", err);
        }
    };

    return (
        <div className="produttivita-container">
            <header className="page-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>Produttività</h2>
                        <p className="header-subtitle">Strumenti operativi per il monitoraggio e la gestione quotidiana</p>
                    </div>
                </div>
            </header>

            {!hasAccess ? (
                <div className="content-area">
                    <div className="coming-soon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <h3>Accesso non autorizzato</h3>
                        <p>Non hai i privilegi necessari per visualizzare questa sezione.</p>
                        <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>Contatta un amministratore per richiedere l'accesso.</p>
                    </div>
                </div>
            ) : (
                <div className="content-area">
                    {/* barra gestione tag */}
                    {isSuperAdmin && (
                        <div className="tag-management-bar">
                            <div className="tag-management-header">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"></path>
                                    <line x1="7" y1="7" x2="7.01" y2="7"></line>
                                </svg>
                                <span>Gestione Tag</span>
                            </div>
                            <div className="tag-management-list">
                                {tags.map(tag => (
                                    <div key={tag.id} className="tag-management-item">
                                        <span className="tag-management-badge" style={getTagStyles(tag.color)}>
                                            {tag.name}
                                        </span>
                                        <span className="tag-management-count">{tag.card_count}</span>
                                        <button
                                            className="tag-management-btn tag-edit-btn"
                                            title="Modifica tag"
                                            onClick={() => handleEditTag(tag)}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </button>
                                        <button
                                            className="tag-management-btn tag-delete-btn"
                                            title="Elimina tag"
                                            onClick={() => handleDeleteTag(tag)}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                <button className="tag-add-btn" onClick={handleCreateTag} title="Nuovo tag">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    Nuovo Tag
                                </button>
                            </div>
                        </div>
                    )}

                    <FiltroCategorie categories={categories} filteredCategory={handleCategoryChange} customCss={"produttivita-section"}/>

                    <div className="pages-grid">
                        {filteredPages.map((page, index) => (
                            <div
                                key={index}
                                className={`page-card ${dropdownCardKey === page.cardKey ? "card-dropdown-open" : ""}`}
                                onClick={() => navigate(page.route)}
                            >
                                <div className="page-card-header">
                                    <h4 className="page-title">{page.title}</h4>
                                    <div className="page-badge-area">
                                        {page.tag ? (
                                            <span className="page-badge" style={getTagStyles(page.tag.color)}>
                                                {page.tag.name}
                                            </span>
                                        ) : (
                                            <span className="page-badge" style={UNTAGGED_STYLES}>
                                                Nessun tag
                                            </span>
                                        )}
                                        {isSuperAdmin && (
                                            <div className="tag-assign-wrapper">
                                                <button
                                                    className="tag-assign-btn"
                                                    title={page.tag ? "Modifica tag" : "Aggiungi tag"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDropdownCardKey(dropdownCardKey === page.cardKey ? null : page.cardKey);
                                                    }}
                                                >
                                                    {page.tag ? (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                                        </svg>
                                                    )}
                                                </button>
                                                {dropdownCardKey === page.cardKey && !isMobile && (
                                                    <TagDropdown
                                                        tags={tags}
                                                        currentTagId={page.tag?.id || null}
                                                        onSelect={(tagId) => handleAssignTag(page.cardKey, tagId)}
                                                        onClose={() => setDropdownCardKey(null)}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="page-description">{page.description}</p>
                                <div className="page-card-footer operations-color">
                                    <span>Apri pagina</span>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* modale assegna tag (mobile) */}
            {dropdownCardKey && isMobile && (
                <div className="tag-modal-overlay" onClick={() => setDropdownCardKey(null)}>
                    <div className="tag-modal" onClick={e => e.stopPropagation()}>
                        <h3>Seleziona tag</h3>
                        <div className="tag-assign-modal-list">
                            {tags.map(tag => (
                                <button
                                    key={tag.id}
                                    className={`tag-assign-modal-item ${
                                        tag.id === (filteredPages.find(p => p.cardKey === dropdownCardKey)?.tag?.id) ? "active" : ""
                                    }`}
                                    onClick={() => handleAssignTag(dropdownCardKey, tag.id)}
                                >
                                    <span className="tag-dropdown-color" style={{ backgroundColor: tag.color }}></span>
                                    {tag.name}
                                </button>
                            ))}
                            {filteredPages.find(p => p.cardKey === dropdownCardKey)?.tag && (
                                <button
                                    className="tag-assign-modal-item tag-assign-modal-remove"
                                    onClick={() => handleAssignTag(dropdownCardKey, null)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    Rimuovi tag
                                </button>
                            )}
                        </div>
                        <div className="tag-modal-actions">
                            <button className="tag-modal-cancel" onClick={() => setDropdownCardKey(null)}>Annulla</button>
                        </div>
                    </div>
                </div>
            )}

            {/* modale creazione/modifica tag */}
            {showTagModal && (
                <TagModal
                    tag={editingTag}
                    onSave={handleSaveTag}
                    onClose={() => { setShowTagModal(false); setEditingTag(null); }}
                />
            )}

            {/* dialog conferma eliminazione */}
            {deleteConfirm && (
                <div className="tag-modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="tag-modal" onClick={e => e.stopPropagation()}>
                        <h3>Elimina tag</h3>
                        <p>
                            Sei sicuro di voler eliminare il tag <strong>{deleteConfirm.name}</strong>?
                        </p>
                        {deleteConfirm.cardCount > 0 && (
                            <p className="tag-delete-warning">
                                {deleteConfirm.cardCount} card perderanno il tag e rimarranno senza.
                            </p>
                        )}
                        <div className="tag-modal-actions">
                            <button className="tag-modal-cancel" onClick={() => setDeleteConfirm(null)}>Annulla</button>
                            <button className="tag-modal-delete" onClick={confirmDeleteTag}>Elimina</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// dropdown per assegnare/rimuovere tag da una card
const TagDropdown = ({ tags, currentTagId, onSelect, onClose }) => {
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [onClose]);

    const handleItemClick = (e, tagId) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(tagId);
    };

    return (
        <div className="tag-dropdown" ref={ref} onClick={e => e.stopPropagation()}>
            {tags.map(tag => (
                <button
                    key={tag.id}
                    className={`tag-dropdown-item ${tag.id === currentTagId ? "active" : ""}`}
                    onClick={(e) => handleItemClick(e, tag.id)}
                >
                    <span className="tag-dropdown-color" style={{ backgroundColor: tag.color }}></span>
                    {tag.name}
                </button>
            ))}
            {currentTagId && (
                <button className="tag-dropdown-item tag-dropdown-remove" onClick={(e) => handleItemClick(e, null)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Rimuovi tag
                </button>
            )}
        </div>
    );
};

// modale per creare o modificare un tag
const TagModal = ({ tag, onSave, onClose }) => {
    const [name, setName] = useState(tag?.name || "");
    const [color, setColor] = useState(tag?.color || TAG_PALETTE[0].color);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSave(name.trim(), color);
    };

    return (
        <div className="tag-modal-overlay" onClick={onClose}>
            <div className="tag-modal" onClick={e => e.stopPropagation()}>
                <h3>{tag ? "Modifica Tag" : "Nuovo Tag"}</h3>
                <form onSubmit={handleSubmit}>
                    <div className="tag-modal-field">
                        <label>Nome</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Es. DKV, WEBFLEET..."
                            autoFocus
                            maxLength={50}
                        />
                    </div>

                    <div className="tag-modal-field">
                        <label>Colore</label>
                        <div className="tag-palette-grid">
                            {TAG_PALETTE.map(p => (
                                <button
                                    key={p.color}
                                    type="button"
                                    className={`tag-palette-swatch ${color === p.color ? "selected" : ""}`}
                                    style={{ backgroundColor: p.color }}
                                    title={p.name}
                                    onClick={() => setColor(p.color)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="tag-modal-field">
                        <label>Anteprima</label>
                        <span className="page-badge" style={getTagStyles(color)}>
                            {name || "Tag"}
                        </span>
                    </div>

                    <div className="tag-modal-actions">
                        <button type="button" className="tag-modal-cancel" onClick={onClose}>Annulla</button>
                        <button type="submit" className="tag-modal-save" disabled={!name.trim()}>Salva</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Produttivita;
